/**
 * Nutrition in points (נקודות) instead of calories and grams.
 *
 * One point is 100 kcal. A macro converts through its own energy — protein and carbs ×4, fats ×9 —
 * and then by the same 100, so 13 g of protein is 0.5 נק׳ and a 590 kcal meal is 5.9. Figures carry
 * one decimal place and no more: 1.6, never 1.67.
 *
 * A company opts in (`companies.nutrition_points_enabled`) and every nutrition figure in the admin
 * changes unit, exactly as it does in the trainee app. Nothing is stored or sent in points: the
 * server carries the flag and this module derives the rest from the calories and grams every
 * response already has. Screens learn the mode from `useNutritionDisplayMode()`.
 *
 * Mirrors `progress-coach/src/helpers/nutritionUnits.js`, so the two clients cannot disagree about
 * what a meal comes to in points.
 *
 * What does NOT convert:
 *  - fibre: the scheme gives it no point value, and converting it would invent a number;
 *  - a food's WEIGHT (`100 גר׳` of chicken, a serving's grams) — that is an amount, not a macro;
 *  - the food dictionary's per-100g catalogue data, which is measured and edited in grams.
 *
 * ## Why each helper returns both the figure AND the unit
 *
 * Call sites render the number and its unit as separate spans (the number coloured, the unit not).
 * A formatter that returned only the number would leave `גר׳` sitting next to a points figure, and
 * nothing would catch it. Returning `{text, unit}` makes the pair impossible to get half-right.
 */

import { GRAM, KCAL, KCAL_SHORT, num } from "./format";

export const CALORIES = "CALORIES" as const;
export const POINTS = "POINTS" as const;
export type NutritionDisplayMode = typeof CALORIES | typeof POINTS;

/** The whole scheme, in one number. */
export const KCAL_PER_POINT = 100;

export const KCAL_PER_GRAM = { protein: 4, carbs: 4, fats: 9 } as const;
export type PointsMacroKey = keyof typeof KCAL_PER_GRAM;

/** `נק׳` — the unit beside a figure. */
export const POINTS_SHORT = "נק׳";
/** `נקודות` — spelled out, for field labels and headers. */
export const POINTS_WORD = "נקודות";

export type Figure = { text: string; unit: string };

const round1 = (n: number): number => Math.round((Number(n) || 0) * 10) / 10;

/** `1,234.5` — comma-grouped, at most one decimal. A whole result prints without `.0`. */
const pointsNum = (n: number): string =>
  round1(n).toLocaleString("he-IL", { maximumFractionDigits: 1 });

export const pointsFromKcal = (kcal: number): number => round1((Number(kcal) || 0) / KCAL_PER_POINT);

export const pointsFromMacro = (key: PointsMacroKey, grams: number): number =>
  round1(((Number(grams) || 0) * KCAL_PER_GRAM[key]) / KCAL_PER_POINT);

/** Exact in this direction: 2.4 נק׳ is 240 kcal, and converting back gives 2.4 again. */
export const kcalFromPoints = (points: number): number => Math.round((Number(points) || 0) * KCAL_PER_POINT);

/**
 * Points back to grams of one macro, for the fields a coach types into.
 *
 * Lossy, unavoidably: 0.7 נק׳ of protein is 17.5 g, and 18 g was also 0.7. A field is either typed
 * in points (and stored as whatever grams it means) or shown from grams and left alone — nothing
 * reads grams, renders points, and writes the grams back unedited.
 */
export const macroFromPoints = (key: PointsMacroKey, points: number): number =>
  round1(((Number(points) || 0) * KCAL_PER_POINT) / KCAL_PER_GRAM[key]);

/**
 * The energy figure — a menu's total, a meal's calories, a food's calories.
 *
 * In points mode this is `kcal / 100` and NOT the sum of the three macro points. The two differ a
 * little (fibre, and the catalogue's own rounding), and of the two the calorie count is the one that
 * is actually measured, so the total stays faithful to it.
 *
 * `short` picks `קל׳` over `קק״ל` in calories mode — the compact form the rows use.
 */
export const energyFigure = (
  kcal: number,
  mode: NutritionDisplayMode,
  { short = false }: { short?: boolean } = {},
): Figure =>
  mode === POINTS
    ? { text: pointsNum(pointsFromKcal(kcal)), unit: POINTS_SHORT }
    : { text: num(kcal), unit: short ? KCAL_SHORT : KCAL };

/**
 * The figure for one macro. `decimals` only applies in calories mode, to keep what each site
 * already showed (most a tenth of a gram, some whole grams). Points are always one decimal.
 */
export const macroFigure = (
  key: PointsMacroKey,
  grams: number,
  mode: NutritionDisplayMode,
  { decimals = 1 }: { decimals?: 0 | 1 } = {},
): Figure =>
  mode === POINTS
    ? { text: pointsNum(pointsFromMacro(key, grams)), unit: POINTS_SHORT }
    : { text: String(decimals === 0 ? Math.round(Number(grams) || 0) : round1(grams)), unit: GRAM };

/** `1,645 קק״ל` / `16.5 נק׳` — for places that print the pair as one string. */
export const energyText = (kcal: number, mode: NutritionDisplayMode, opts?: { short?: boolean }): string => {
  const f = energyFigure(kcal, mode, opts);
  return `${f.text} ${f.unit}`;
};

/** `48 גר׳` / `1.9 נק׳`. */
export const macroText = (
  key: PointsMacroKey,
  grams: number,
  mode: NutritionDisplayMode,
  opts?: { decimals?: 0 | 1 },
): string => {
  const f = macroFigure(key, grams, mode, opts);
  return `${f.text} ${f.unit}`;
};

/** The unit alone — an input's suffix, a column header. */
export const energyUnit = (mode: NutritionDisplayMode, { short = false }: { short?: boolean } = {}): string =>
  mode === POINTS ? POINTS_SHORT : short ? KCAL_SHORT : KCAL;

export const macroUnit = (mode: NutritionDisplayMode): string => (mode === POINTS ? POINTS_SHORT : GRAM);

/** What the energy figure is CALLED: `קלוריות` / `נקודות`. */
export const energyLabel = (mode: NutritionDisplayMode): string => (mode === POINTS ? POINTS_WORD : "קלוריות");

/** A signed deviation from plan (`+120` kcal / `+1.2` points). The drift is a calorie count. */
export const deviationValue = (kcal: number, mode: NutritionDisplayMode): number =>
  mode === POINTS ? pointsFromKcal(kcal) : Math.round(Number(kcal) || 0);

// ── Inputs ──────────────────────────────────────────────────────────────────────────────────────
// A field holds a value in the unit the coach types in; the model stays in kcal and grams. Convert
// on the way in and on the way out, at the field — never round-trip a value nobody edited.

/** kcal → the number an energy field shows. */
export const energyToInput = (kcal: number, mode: NutritionDisplayMode): number =>
  mode === POINTS ? pointsFromKcal(kcal) : Number(kcal) || 0;

/** What a coach typed in an energy field → kcal. */
export const energyFromInput = (value: number, mode: NutritionDisplayMode): number =>
  mode === POINTS ? kcalFromPoints(value) : Number(value) || 0;

/** grams → the number a macro field shows. */
export const macroToInput = (key: PointsMacroKey, grams: number, mode: NutritionDisplayMode): number =>
  mode === POINTS ? pointsFromMacro(key, grams) : Number(grams) || 0;

/** What a coach typed in a macro field → grams. */
export const macroFromInput = (key: PointsMacroKey, value: number, mode: NutritionDisplayMode): number =>
  mode === POINTS ? macroFromPoints(key, value) : Number(value) || 0;
